import type { OmitNull } from '@ipfabric/utils/types/omitNull';
import type { ReactNode } from 'react';
import type { VariantProps } from 'tailwind-variants';
import type { ListItemProps } from '../ListItem/ListItem.types';
import type { floatingListItemVariants } from './FloatingListItem.styles';

type FloatingListItemBaseProps = {
  className?: string;
  dataTestId?: string;
  description?: string;
  endAdornment?: ReactNode;
  onClick?: () => void;
  preventMenuClose?: boolean; // Prevents the FloatingList menu from closing on click
  selected?: boolean;
  startAdornment?: ReactNode;
} & FloatingListItemVariantProps &
  Pick<ListItemProps, 'size'>;

export type FloatingListItemAnchorProps = FloatingListItemBaseProps & {
  label: string;
  href: string;
  customContent?: never;
};

export type FloatingListItemContainerProps = FloatingListItemBaseProps & {
  customContent?: never;
  dataTestId?: string;
  href?: never;
  label: string;
  onClick: () => void;
};

export type FloatingListItemWithCustomContentProps =
  FloatingListItemBaseProps & {
    customContent: ReactNode;
    label?: never;
    href?: never;
  };

export type FloatingListDividerProps = {
  divider: boolean;
};

export type FloatingListItemProps =
  | FloatingListItemAnchorProps
  | FloatingListItemContainerProps
  | FloatingListItemWithCustomContentProps;

export type FloatingListItem = FloatingListItemProps | FloatingListDividerProps;

export type FloatingListProps = {
  initialSelectedIndex?: number;
  items: FloatingListItem[];
  children: (
    setRef: (node: HTMLElement | null) => void,
    setIsOpen: (value: boolean) => void,
    open: boolean,
  ) => ReactNode;
} & Pick<ListItemProps, 'size'>;

type FloatingListItemOrDividerProps =
  | FloatingListItemProps
  | FloatingListDividerProps;

export function isFloatingListItemAnchor(
  props: FloatingListItemOrDividerProps,
): props is FloatingListItemAnchorProps {
  return 'href' in props && typeof props.href === 'string';
}

export const isFloatingListItemContainer = (
  item: FloatingListItemOrDividerProps,
): item is FloatingListItemContainerProps => {
  return 'onClick' in item && typeof item.onClick === 'function';
};

export const isFloatingListItemDivider = (
  item: FloatingListItemOrDividerProps,
): item is FloatingListDividerProps => {
  return 'divider' in item;
};

export type FloatingListItemVariantProps = OmitNull<
  VariantProps<typeof floatingListItemVariants>
>;
